/**
 * Allys Project Showcase
 * 
 * Theme-specific implementation of ProjectShowcase with Allys styling
 */

'use client';

import React from 'react';
import { ProjectShowcase, type ProjectShowcaseProps } from '../../../../src/components/projects';

export type AllysProjectShowcaseProps = ProjectShowcaseProps;

/**
 * AllysProjectShowcase - Styled wrapper for ProjectShowcase
 * 
 * Applies Allys theme colors and Inter font styling to the core component
 */
export function AllysProjectShowcase(props: AllysProjectShowcaseProps) {
  // Allys theme colors
  const allysColors = {
    primary: '#243462',
    secondary: '#040813',
    accent: '#BBA339',
  };

  // Use brand colors if provided, otherwise use Allys defaults
  const brandColors = {
    primary: props.brandColors?.primary || allysColors.primary,
    secondary: props.brandColors?.secondary || allysColors.secondary,
    accent: props.brandColors?.accent || allysColors.accent,
  };

  return (
    <div 
      className="font-inter"
    >
      <ProjectShowcase
        {...props}
        brandColors={brandColors}
        renderMediaButton={(buttonProps) => (
          <button
            onClick={buttonProps.onClick}
            className={`rounded-xl px-6 py-3 text-sm font-semibold transition-all ${
              buttonProps.isActive
                ? 'shadow-xl'
                : 'bg-white/20 backdrop-blur-md hover:bg-white/30'
            }`}
            style={buttonProps.isActive ? { 
              backgroundColor: allysColors.accent, 
              color: '#fff' 
            } : { 
              color: '#fff' 
            }}
          >
            {buttonProps.label}
          </button>
        )}
        renderUnitCard={(cardProps) => (
          <div className="rounded-2xl bg-white p-8 shadow-2xl">
            {/* Unit Availability */}
            <div className="mb-6">
              <h3 
                className="mb-4 text-2xl font-bold"
                style={{ color: allysColors.primary }}
              >
                {cardProps.messages.availableUnits}
              </h3>
              
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="mb-2 flex justify-between text-sm font-medium text-gray-600">
                  <span>{cardProps.project.availableUnits || 0} Available</span>
                  <span>{cardProps.project.soldUnits || 0} Sold</span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${cardProps.project.totalUnits && cardProps.project.soldUnits 
                        ? ((cardProps.project.totalUnits - cardProps.project.soldUnits) / cardProps.project.totalUnits) * 100 
                        : 0}%`,
                      backgroundColor: allysColors.accent,
                    }}
                  />
                </div>
                <div className="mt-2 text-center text-xs font-medium text-gray-500">
                  {cardProps.project.totalUnits && cardProps.project.soldUnits 
                    ? Math.round(((cardProps.project.totalUnits - cardProps.project.soldUnits) / cardProps.project.totalUnits) * 100)
                    : 0}% {cardProps.messages.availableUnits}
                </div>
              </div>

              {/* Unit Stats */}
              <div className="grid grid-cols-3 gap-4 border-t border-gray-200 pt-4">
                <div className="text-center">
                  <div 
                    className="text-3xl font-bold"
                    style={{ color: allysColors.accent }}
                  >
                    {cardProps.project.totalUnits || 0}
                  </div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {cardProps.messages.totalUnits}
                  </div>
                </div>
                <div className="text-center">
                  <div 
                    className="text-3xl font-bold"
                    style={{ color: allysColors.accent }}
                  >
                    {cardProps.project.availableUnits || 0}
                  </div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Available
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-400">
                    {cardProps.project.soldUnits || 0}
                  </div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Sold
                  </div>
                </div>
              </div>
            </div>

            {/* Unit Mix Summary */}
            {cardProps.project.unitMixSummary && (
              <div className="mb-6 rounded-lg bg-gray-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                  Unit Mix
                </div>
                <div className="text-sm text-gray-700">
                  {cardProps.project.unitMixSummary}
                </div>
              </div>
            )}

            {/* CTA Button */}
            <button
              className="w-full rounded-xl py-4 text-center text-base font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]"
              style={{ backgroundColor: allysColors.primary }}
            >
              {cardProps.messages.enquireNow}
            </button>

            {/* Developer Info */}
            {cardProps.project.developerOrganisationId && (
              <div className="mt-4 text-center text-xs text-gray-500">
                {cardProps.messages.developedBy}
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
}
